import type { Graphics } from 'pixi.js';
import { clamp01, frac, lerp, smoothstep, wrap } from '../../shared/scalar';
import { BEATS_PER_BAR, MAIN_TEMPO, type Tempo } from '../../shared/tempo';
import { elementOfTick } from '../../sim/full/attacks/cycle';
import type { PlayerState } from '../../sim/state';
import type { Frame } from '../frame';
import { TAU } from '../paint';
import { mixColor } from '../palette';
import { REFERENCE_RADIUS } from '../textures-looks';
import { ELEMENT_COLORS, UNIT } from './avatar-paint';

const WHITE = 0xffffff;
const OUT = 0x04060f;
const POLE_COLOR = 0xf6ecd2;

const MOTION = {
  normal: { beats: 2, swing: 1, change: 0.25, flash: 0.45 },
  calm: { beats: 4, swing: 0.35, change: 0.5, flash: 0 },
} as const;
// Both flags whip forward together on the 1 of each bar, as the element turns.
const WHIP = { at: 0.1, width: 0.17, sweep: 0.35, lean: 1.1 } as const;
const SHOULDER = { along: 3.3, across: 9.2 } as const;
const SWEEP = { rest: -0.25, swing: 1, lean: 0.3 } as const;
const REACH = { base: 5.8, swing: 2.5 } as const;
const POLE = { length: 15.8, swing: 2.1, outline: 1.5, core: 0.75, knob: 0.9, rim: 0.4 } as const;
const ARM = { outline: 3.6, skin: 2.5 } as const;
const LAG = { hand: 0.14, tip: 0.32 } as const;
const SILK = {
  steps: 12,
  narrow: 4.2,
  wide: 9.2,
  whites: [0.4, 0.22, 0.05],
  outline: 0.55,
  lines: [0.3, 0.55, 0.8],
  line: 0.3,
  lineWhite: 0.7,
  lineAlpha: 0.55,
} as const;
const RIBBON = {
  at: 0.85,
  trail: 1.2,
  steps: 24,
  width: 7.9,
  taper: 0.6,
  chunk: 2,
  lineChunk: 6,
  line: 0.3,
  lineWhite: 0.6,
  outline: 0.4,
  outlineAlpha: 0.6,
  fade: { head: 0.95, tail: 0.7 },
} as const;
const CURL = { steps: 12, radius: 3.3, turns: 1.3, shrink: 0.75, width: 0.4, thin: 0.85 } as const;
const BANDS = [
  { shift: 0, size: 1, white: 0, lines: [0.2, 0.4] },
  { shift: -0.22, size: 0.5, white: 0.25, lines: [0.3, 0.65] },
] as const;
const SIDES = [-1, 1] as const;

const POINTS = RIBBON.steps + 1 + CURL.steps;
const CHUNKS = (POINTS - 1) / RIBBON.chunk;
const LINE_CHUNKS = (POINTS - 1) / RIBBON.lineChunk;
const SILK_POINTS = SILK.steps + 1;

// Body units, along the facing and across it.
export interface FlagPose {
  handAlong: number;
  handAcross: number;
  tipAlong: number;
  tipAcross: number;
}

export interface FlagMotion {
  x: number;
  y: number;
  facing: number;
  size: number;
  beats: number;
  calm: boolean;
  tempo: Tempo;
  teint: number;
}

export type FlagHold = Pick<FlagMotion, 'x' | 'y' | 'facing' | 'size' | 'beats' | 'calm'>;

export interface Point {
  x: number;
  y: number;
}

const held: FlagPose = { handAlong: 0, handAcross: 0, tipAlong: 0, tipAcross: 0 };

// The dancer as the players family places him, so what leaves his flags leaves where they are drawn.
export function holdOf(player: PlayerState, alpha: number, frame: Frame, into: FlagHold): FlagHold {
  const { tempo } = frame;
  into.x = lerp(player.prevX, player.x, alpha);
  into.y = lerp(player.prevY, player.y, alpha);
  into.facing = Math.atan2(player.aim.y, player.aim.x);
  into.size = player.radius / REFERENCE_RADIUS;
  into.beats = (frame.now - tempo.originTick) / tempo.ticksPerBeat;
  into.calm = frame.calm;
  return into;
}

export function flagTip(side: number, hold: FlagHold, into: Point): Point {
  const pose = flagPose(side, hold.beats, hold.calm, held);
  const unit = UNIT * hold.size;
  const cos = Math.cos(hold.facing);
  const sin = Math.sin(hold.facing);
  into.x = hold.x + (pose.tipAlong * cos - pose.tipAcross * sin) * unit;
  into.y = hold.y + (pose.tipAlong * sin + pose.tipAcross * cos) * unit;
  return into;
}

export function flagWhip(beats: number, calm: boolean): number {
  const toOne = wrap(beats + BEATS_PER_BAR / 2, BEATS_PER_BAR) - BEATS_PER_BAR / 2;
  const motion = calm ? MOTION.calm : MOTION.normal;
  return motion.swing * Math.exp(-(((toOne - WHIP.at) / WHIP.width) ** 2));
}

// One figure-eight per flag over `beats` beats: the sweep swings once while the reach breathes twice.
export function flagPose(side: number, beats: number, calm: boolean, into: FlagPose): FlagPose {
  const motion = calm ? MOTION.calm : MOTION.normal;
  const turn = (TAU * beats) / motion.beats;
  const kick = flagWhip(beats, calm);
  const sweep = SWEEP.rest + SWEEP.swing * motion.swing * Math.sin(turn) - WHIP.sweep * kick;
  const reach = REACH.base + REACH.swing * motion.swing * Math.sin(2 * turn);
  const lean = sweep + SWEEP.lean * motion.swing * Math.cos(turn) - WHIP.lean * kick;
  const length = POLE.length + POLE.swing * motion.swing * Math.sin(2 * turn);
  into.handAlong = SHOULDER.along - Math.sin(sweep) * reach;
  into.handAcross = side * (SHOULDER.across + Math.cos(sweep) * reach);
  into.tipAlong = into.handAlong - Math.sin(lean) * length;
  into.tipAcross = into.handAcross + side * Math.cos(lean) * length;
  return into;
}

// The silk turns to the element of the bar on its 1 with a brief white flash between the two, never a muddy blend.
export function flagColor(beats: number, calm: boolean, tempo: Tempo): number {
  const motion = calm ? MOTION.calm : MOTION.normal;
  const element = elementOfTick(tempo.originTick + beats * tempo.ticksPerBeat, tempo);
  const lit = ELEMENT_COLORS[element] ?? ELEMENT_COLORS[0];
  const before = ELEMENT_COLORS[(element + ELEMENT_COLORS.length - 1) % ELEMENT_COLORS.length];
  const turned = smoothstep(clamp01((BEATS_PER_BAR * frac(beats / BEATS_PER_BAR)) / motion.change));
  const blend = mixColor(before ?? lit, lit, turned);
  return mixColor(blend, WHITE, motion.flash * Math.sin(Math.PI * turned));
}

function pool(count: number, length: number): number[][] {
  return Array.from({ length: count }, () => new Array<number>(length).fill(0));
}

// Pixi keeps the points of a path until it draws it: every polygon has its own array.
export class Flags {
  readonly hands = new Float64Array(4);
  readonly tips = new Float64Array(4);
  color = WHITE;
  private readonly pose: FlagPose = { handAlong: 0, handAcross: 0, tipAlong: 0, tipAcross: 0 };
  private readonly path = new Float64Array(POINTS * 2);
  private readonly widths = new Float64Array(POINTS);
  private readonly times = new Float64Array(POINTS);
  private readonly normals = new Float64Array(POINTS * 2);
  private readonly outer = new Float64Array(POINTS * 2);
  private readonly inner = new Float64Array(POINTS * 2);
  private readonly lead = new Float64Array(SILK_POINTS * 2);
  private readonly trail = new Float64Array(SILK_POINTS * 2);
  private readonly chunks = pool(2 * BANDS.length * CHUNKS, (RIBBON.chunk + 1) * 4);
  private readonly lines = pool(2 * BANDS.length * 2 * LINE_CHUNKS, (RIBBON.lineChunk + 1) * 2);
  private readonly outlines = pool(2 * BANDS.length, POINTS * 4);
  private readonly strips = pool(2 * SILK.whites.length, SILK_POINTS * 4);
  private readonly silks = pool(2, SILK_POINTS * 4);
  private x = 0;
  private y = 0;
  private cos = 1;
  private sin = 0;
  private unit = UNIT;
  private calm = false;
  // A slower eight trails as far behind in its own time, so its silk and ribbon keep their shape.
  private pace = 1;
  private tempo: Tempo = MAIN_TEMPO;

  draw(under: Graphics, over: Graphics, motion: FlagMotion): void {
    const { x, y, facing, size, beats, calm, tempo, teint } = motion;
    this.x = x;
    this.y = y;
    this.cos = Math.cos(facing);
    this.sin = Math.sin(facing);
    this.unit = UNIT * size;
    this.calm = calm;
    this.pace = (calm ? MOTION.calm : MOTION.normal).beats / MOTION.normal.beats;
    this.tempo = tempo;
    this.color = flagColor(beats, calm, tempo);
    under.clear();
    over.clear();
    SIDES.forEach((side, index) => {
      this.ribbon(under, side, index, beats);
    });
    SIDES.forEach((side, index) => {
      this.arm(under, side, index, beats, teint);
      this.silk(over, side, index, beats);
    });
  }

  private worldX(along: number, across: number): number {
    return this.x + (along * this.cos - across * this.sin) * this.unit;
  }

  private worldY(along: number, across: number): number {
    return this.y + (along * this.sin + across * this.cos) * this.unit;
  }

  private poleAt(side: number, beats: number, share: number, into: Float64Array, at: number): void {
    const pose = flagPose(side, beats, this.calm, this.pose);
    const along = lerp(pose.handAlong, pose.tipAlong, share);
    const across = lerp(pose.handAcross, pose.tipAcross, share);
    into[at] = this.worldX(along, across);
    into[at + 1] = this.worldY(along, across);
  }

  private colorAt(beats: number): number {
    return flagColor(beats, this.calm, this.tempo);
  }

  private arm(g: Graphics, side: number, index: number, beats: number, teint: number): void {
    const pose = flagPose(side, beats, this.calm, this.pose);
    const sx = this.worldX(SHOULDER.along, side * SHOULDER.across);
    const sy = this.worldY(SHOULDER.along, side * SHOULDER.across);
    const hx = this.worldX(pose.handAlong, pose.handAcross);
    const hy = this.worldY(pose.handAlong, pose.handAcross);
    this.hands[index * 2] = hx;
    this.hands[index * 2 + 1] = hy;
    this.tips[index * 2] = this.worldX(pose.tipAlong, pose.tipAcross);
    this.tips[index * 2 + 1] = this.worldY(pose.tipAlong, pose.tipAcross);
    g.moveTo(sx, sy)
      .lineTo(hx, hy)
      .stroke({ width: ARM.outline * this.unit, color: OUT, cap: 'round' });
    g.moveTo(sx, sy)
      .lineTo(hx, hy)
      .stroke({ width: ARM.skin * this.unit, color: teint, cap: 'round' });
  }

  // The path of a point of the pole over the last beat, curling into a spiral at its end.
  private ribbon(g: Graphics, side: number, index: number, beats: number): void {
    const { path, widths, times, normals, outer, inner } = this;
    const width = RIBBON.width * this.unit;
    const from = beats - lerp(LAG.hand, LAG.tip, RIBBON.at) * this.pace;
    const to = beats - RIBBON.trail * this.pace;
    for (let step = 0; step <= RIBBON.steps; step += 1) {
      const share = step / RIBBON.steps;
      const when = lerp(from, to, share);
      this.poleAt(side, when, RIBBON.at, path, step * 2);
      widths[step] = width * (1 - RIBBON.taper * share);
      times[step] = when;
    }
    const end = RIBBON.steps * 2;
    const ex = path[end] ?? 0;
    const ey = path[end + 1] ?? 0;
    const dx = ex - (path[end - 4] ?? 0);
    const dy = ey - (path[end - 3] ?? 0);
    const length = Math.hypot(dx, dy) || 1;
    const radius = CURL.radius * this.unit;
    const cx = ex - (dy / length) * side * radius;
    const cy = ey + (dx / length) * side * radius;
    const start = Math.atan2(ey - cy, ex - cx);
    for (let step = 1; step <= CURL.steps; step += 1) {
      const share = step / CURL.steps;
      const at = RIBBON.steps + step;
      const r = radius * (1 - CURL.shrink * share);
      const angle = start + side * share * CURL.turns * TAU;
      path[at * 2] = cx + Math.cos(angle) * r;
      path[at * 2 + 1] = cy + Math.sin(angle) * r;
      widths[at] = width * CURL.width * (1 - CURL.thin * share);
      times[at] = to;
    }
    for (let point = 0; point < POINTS; point += 1) {
      const ahead = Math.min(point + 1, POINTS - 1) * 2;
      const behind = Math.max(point - 1, 0) * 2;
      const nx = (path[ahead] ?? 0) - (path[behind] ?? 0);
      const ny = (path[ahead + 1] ?? 0) - (path[behind + 1] ?? 0);
      const d = Math.hypot(nx, ny) || 1;
      normals[point * 2] = -ny / d;
      normals[point * 2 + 1] = nx / d;
    }
    BANDS.forEach((band, bandIndex) => {
      const shift = band.shift * side;
      for (let point = 0; point < POINTS; point += 1) {
        const w = widths[point] ?? 0;
        const px = path[point * 2] ?? 0;
        const py = path[point * 2 + 1] ?? 0;
        const nx = normals[point * 2] ?? 0;
        const ny = normals[point * 2 + 1] ?? 0;
        const out = w * (shift + band.size / 2);
        const off = w * (shift - band.size / 2);
        outer[point * 2] = px + nx * out;
        outer[point * 2 + 1] = py + ny * out;
        inner[point * 2] = px + nx * off;
        inner[point * 2 + 1] = py + ny * off;
      }
      const slot = index * BANDS.length + bandIndex;
      this.bandFill(g, slot, band.white);
      this.bandLines(g, slot, band.lines);
      const outline = this.outlines[slot] ?? [];
      for (let point = 0; point < POINTS; point += 1) {
        const back = (2 * POINTS - 1 - point) * 2;
        outline[point * 2] = outer[point * 2] ?? 0;
        outline[point * 2 + 1] = outer[point * 2 + 1] ?? 0;
        outline[back] = inner[point * 2] ?? 0;
        outline[back + 1] = inner[point * 2 + 1] ?? 0;
      }
      g.poly(outline).stroke({
        width: RIBBON.outline * this.unit,
        color: OUT,
        alpha: RIBBON.outlineAlpha,
        join: 'round',
      });
    });
  }

  private fadeAt(point: number): number {
    return RIBBON.fade.head - RIBBON.fade.tail * Math.min(1, point / RIBBON.steps);
  }

  private bandFill(g: Graphics, slot: number, white: number): void {
    const { outer, inner, times } = this;
    for (let chunk = 0; chunk < CHUNKS; chunk += 1) {
      const first = chunk * RIBBON.chunk;
      const polygon = this.chunks[slot * CHUNKS + chunk] ?? [];
      const back = 2 * (RIBBON.chunk + 1) - 1;
      for (let step = 0; step <= RIBBON.chunk; step += 1) {
        const point = first + step;
        polygon[step * 2] = outer[point * 2] ?? 0;
        polygon[step * 2 + 1] = outer[point * 2 + 1] ?? 0;
        polygon[(back - step) * 2] = inner[point * 2] ?? 0;
        polygon[(back - step) * 2 + 1] = inner[point * 2 + 1] ?? 0;
      }
      const middle = first + RIBBON.chunk / 2;
      const color = mixColor(
        this.colorAt((times[middle] ?? 0) + LAG.tip * this.pace),
        WHITE,
        white,
      );
      g.poly(polygon).fill({ color, alpha: this.fadeAt(middle) });
    }
  }

  private bandLines(g: Graphics, slot: number, shares: readonly number[]): void {
    const { outer, inner, times } = this;
    shares.forEach((share, lineIndex) => {
      for (let chunk = 0; chunk < LINE_CHUNKS; chunk += 1) {
        const first = chunk * RIBBON.lineChunk;
        const line = this.lines[(slot * 2 + lineIndex) * LINE_CHUNKS + chunk] ?? [];
        for (let step = 0; step <= RIBBON.lineChunk; step += 1) {
          const point = first + step;
          line[step * 2] = lerp(outer[point * 2] ?? 0, inner[point * 2] ?? 0, share);
          line[step * 2 + 1] = lerp(outer[point * 2 + 1] ?? 0, inner[point * 2 + 1] ?? 0, share);
        }
        const middle = first + RIBBON.lineChunk / 2;
        const color = mixColor(
          this.colorAt((times[middle] ?? 0) + LAG.tip * this.pace),
          WHITE,
          RIBBON.lineWhite,
        );
        g.poly(line, false).stroke({
          width: RIBBON.line * this.unit,
          color,
          alpha: this.fadeAt(middle),
          cap: 'round',
          join: 'round',
        });
      }
    });
  }

  // The flag itself: the silk between the pole now and the pole a moment ago, never folded thinner than a flag.
  private silk(g: Graphics, side: number, index: number, beats: number): void {
    const { lead, trail, color, unit } = this;
    for (let step = 0; step < SILK_POINTS; step += 1) {
      const share = step / SILK.steps;
      this.poleAt(side, beats, share, lead, step * 2);
      this.poleAt(side, beats - lerp(LAG.hand, LAG.tip, share) * this.pace, share, trail, step * 2);
      const hx = lead[step * 2] ?? 0;
      const hy = lead[step * 2 + 1] ?? 0;
      const dx = (trail[step * 2] ?? 0) - hx;
      const dy = (trail[step * 2 + 1] ?? 0) - hy;
      const d = Math.hypot(dx, dy) || 1;
      const reach = Math.max(d, lerp(SILK.narrow, SILK.wide, share) * unit) / d;
      trail[step * 2] = hx + dx * reach;
      trail[step * 2 + 1] = hy + dy * reach;
    }
    SILK.whites.forEach((white, strip) => {
      const near = strip / SILK.whites.length;
      const far = (strip + 1) / SILK.whites.length;
      this.silkBand(this.strips[index * SILK.whites.length + strip] ?? [], near, far);
      g.poly(this.strips[index * SILK.whites.length + strip] ?? []).fill(
        mixColor(color, WHITE, white),
      );
    });
    const outline = this.silks[index] ?? [];
    this.silkBand(outline, 0, 1);
    g.poly(outline).stroke({ width: SILK.outline * unit, color: OUT, join: 'round' });
    const lineColor = mixColor(color, WHITE, SILK.lineWhite);
    for (const share of SILK.lines) {
      const step = Math.round(share * SILK.steps) * 2;
      g.moveTo(lead[step] ?? 0, lead[step + 1] ?? 0)
        .lineTo(trail[step] ?? 0, trail[step + 1] ?? 0)
        .stroke({ width: SILK.line * unit, color: lineColor, alpha: SILK.lineAlpha, cap: 'round' });
    }

    const hand = flagPose(side, beats, this.calm, this.pose);
    const hx = this.worldX(hand.handAlong, hand.handAcross);
    const hy = this.worldY(hand.handAlong, hand.handAcross);
    const tx = this.worldX(hand.tipAlong, hand.tipAcross);
    const ty = this.worldY(hand.tipAlong, hand.tipAcross);
    g.moveTo(hx, hy)
      .lineTo(tx, ty)
      .stroke({ width: POLE.outline * unit, color: OUT, cap: 'round' });
    g.moveTo(hx, hy)
      .lineTo(tx, ty)
      .stroke({ width: POLE.core * unit, color: POLE_COLOR, cap: 'round' });
    g.circle(tx, ty, POLE.knob * unit)
      .fill(POLE_COLOR)
      .stroke({ width: POLE.rim * unit, color: OUT });
  }

  private silkBand(into: number[], near: number, far: number): void {
    const { lead, trail } = this;
    const back = 2 * SILK_POINTS - 1;
    for (let step = 0; step < SILK_POINTS; step += 1) {
      const lx = lead[step * 2] ?? 0;
      const ly = lead[step * 2 + 1] ?? 0;
      const tx = trail[step * 2] ?? 0;
      const ty = trail[step * 2 + 1] ?? 0;
      into[step * 2] = lerp(lx, tx, near);
      into[step * 2 + 1] = lerp(ly, ty, near);
      into[(back - step) * 2] = lerp(lx, tx, far);
      into[(back - step) * 2 + 1] = lerp(ly, ty, far);
    }
  }
}
